import { type LoginInput, loginSchema, type MagicLinkRequestInput,magicLinkRequestSchema } from '@emailforge/schemas';
import { Button, Input } from '@emailforge/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { Icon } from '@/components/ui/icons';
import { setSession } from '@/features/auth/authSlice';
import { identify } from '@/lib/monitoring';
import { useAppDispatch } from '@/store/hooks';

import { AuthBrandSide } from './AuthBrandSide';
import { OAuthButtons } from './OAuthButtons';

export function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [sent, setSent] = useState(false);

  return (
    <div style={{ minHeight: '100vh', display: 'flex' }}>
      <AuthBrandSide
        title={
          <>
            Welcome <i style={{ color: 'var(--flame-2)' }}>back</i>.
          </>
        }
        sub="Engineer-grade email infrastructure. Magic links, audit-grade logs, and a sending pipeline that doesn't go down on launch day."
        testimonial={{
          q: 'Emailforge is what I wish my last three companies had — the editor, the audit trail, the SDK, all in one place.',
          n: 'Lina Park',
          t: 'Staff engineer · Latch',
          initials: 'LP',
          color: 'oklch(0.65 0.18 35)',
        }}
      />

      <div
        style={{
          flex: 1,
          background: 'var(--paper)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px',
        }}
      >
        <div style={{ width: '100%', maxWidth: 400 }}>
          {sent ? (
            <MagicLinkSent onReset={() => setSent(false)} />
          ) : (
            <>
              <h1 className="display" style={{ fontSize: 34, margin: '0 0 8px', letterSpacing: -0.02 }}>
                Sign in
              </h1>
              <p style={{ fontSize: 13.5, color: 'var(--ink-3)', margin: 0, marginBottom: 24 }}>
                New to Emailforge?{' '}
                <Link to="/signup" style={{ color: 'var(--flame-2)', textDecoration: 'none', fontWeight: 500 }}>
                  Create a workspace
                </Link>
              </p>

              {!showPassword && (
                <MagicLinkPrimaryBox onSent={() => setSent(true)} />
              )}

              {showPassword && <PasswordForm />}

              <Divider />

              <OAuthButtons />

              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={{
                  marginTop: 22,
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  fontSize: 12.5,
                  color: 'var(--ink-3)',
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                {showPassword ? '← Use magic link instead' : 'Use password instead →'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MagicLinkPrimaryBox({ onSent }: { onSent: () => void }) {
  return (
    <div
      style={{
        position: 'relative',
        padding: '20px 18px 18px',
        border: '1px solid var(--flame-line)',
        borderRadius: 'var(--r-3)',
        background: 'var(--flame-soft)',
        marginBottom: 18,
      }}
    >
      <div
        className="mono"
        style={{
          position: 'absolute',
          top: -9,
          left: 14,
          background: 'var(--paper)',
          padding: '0 8px',
          fontSize: 9.5,
          letterSpacing: 0.1,
          textTransform: 'uppercase',
          color: 'var(--flame-2)',
          fontWeight: 600,
        }}
      >
        Recommended
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}>
        Magic link
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14, lineHeight: 1.5 }}>
        We'll email you a one-time link. Valid for 15 minutes — no password to remember.
      </div>
      <MagicLinkForm onSent={onSent} />
    </div>
  );
}

function Divider() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '22px 0' }}>
      <div className="mf-div" style={{ flex: 1 }} />
      <span
        className="mono"
        style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.08, textTransform: 'uppercase' }}
      >
        or
      </span>
      <div className="mf-div" style={{ flex: 1 }} />
    </div>
  );
}

function MagicLinkForm({ onSent }: { onSent: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<MagicLinkRequestInput>({
    resolver: zodResolver(magicLinkRequestSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: MagicLinkRequestInput) => {
    setError(null);
    try {
      const res = await fetch('/api/auth/magic-link', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      onSent();
    } catch (err) {
      setError((err as Error).message ?? 'Could not send the magic link.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Field label="Email" error={errors.email?.message}>
        <Input type="email"
          
          style={{ height: 40 }}
          autoFocus
          placeholder="you@company.com"
          autoComplete="email"
          {...register('email')} />
      </Field>

      <Button variant="flame" style={{ height: 42, justifyContent: 'center' }}
        disabled={!isValid || isSubmitting}
        type="submit">
        {isSubmitting ? 'Sending link…' : 'Send magic link'}
        <Icon.arrow size={14} />
      </Button>

      {error && <ErrorBox>{error}</ErrorBox>}
    </form>
  );
}

function PasswordForm() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: LoginInput) => {
    setError(null);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? 'Invalid email or password');
      }
      const { accessToken, refreshToken } = (await res.json()) as {
        accessToken: string;
        refreshToken?: string;
      };

      const meRes = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!meRes.ok) throw new Error('Could not hydrate session');
      const me = await meRes.json();

      dispatch(setSession({ me, accessToken, refreshToken }));
      identify(me.user.id, { email: me.user.email });
      const branding = me.memberships?.[0]?.branding;
      if (branding) {
        const { applyBranding } = await import('@/features/branding/applyBranding');
        applyBranding(branding);
      }
      navigate('/app', { replace: true });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Field label="Email" error={errors.email?.message}>
        <Input type="email"
          
          style={{ height: 40 }}
          autoFocus
          placeholder="you@company.com"
          autoComplete="email"
          {...register('email')} />
      </Field>

      <Field label="Password" error={errors.password?.message}>
        <Input type="password"
          
          style={{ height: 40 }}
          placeholder="Your password"
          autoComplete="current-password"
          {...register('password')} />
      </Field>

      <Button variant="flame" style={{ height: 42, justifyContent: 'center' }}
        disabled={!isValid || isSubmitting}
        type="submit">
        {isSubmitting ? 'Signing in…' : 'Sign in'}
        <Icon.arrow size={14} />
      </Button>

      {error && <ErrorBox>{error}</ErrorBox>}
    </form>
  );
}

function MagicLinkSent({ onReset }: { onReset: () => void }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 'var(--r-3)',
          background: 'var(--spark-soft)',
          border: '1px solid var(--spark-line)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--spark)',
          marginBottom: 20,
        }}
      >
        <Icon.check size={22} />
      </div>
      <h1 className="display" style={{ fontSize: 28, margin: '0 0 10px' }}>
        Check your inbox
      </h1>
      <p style={{ fontSize: 13.5, color: 'var(--ink-3)', lineHeight: 1.6 }}>
        We sent a magic link. Click it to sign in — it works for 15 minutes.
      </p>
      <Button onClick={onReset}  style={{ marginTop: 20 }}>
        Resend or use another email
      </Button>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        className="mono"
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          color: 'var(--ink-3)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
      {error && <div style={{ fontSize: 12, color: 'var(--err)', marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--err-soft)',
        color: 'var(--err)',
        padding: '10px 12px',
        borderRadius: 'var(--r-2)',
        fontSize: 12.5,
        border: '1px solid oklch(0.85 0.10 25)',
      }}
    >
      {children}
    </div>
  );
}
